// Real HTTP adapter for Management Guidelines (FEATURE_030). Wired to
// kr.co.ards.admin.guideline.GuidelineController @ /api/v1/admin/management-guidelines.
// Maps the server error envelope { error: { code, message, fields, meta } } to
// ManagementGuidelineApiError. Activated by setting VITE_USE_MOCK_API=false;
// see management-guidelines.adapter.ts.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import type { ProDomain } from '@/features/pro-item-design/types/pro-item-design.types';
import {
  ManagementGuidelineApiError,
  type CreateGuidelineInput,
  type DeleteGuidelineResult,
  type ManagementGuideline,
  type ManagementGuidelineApi,
  type ManagementGuidelineApiErrorCode,
  type ManagementGuidelineDetail,
  type ProTree,
  type UpdateGuidelineInput,
} from '../types/management-guidelines.types';
import { domainsToProTree } from '../utils/topic-tree';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  // Already-typed errors (e.g. the array-guard throws below) pass straight
  // through — they have no axios `response` and must not be misread as NETWORK.
  if (e instanceof ManagementGuidelineApiError) throw e;
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // No `response` means the request never reached the server (offline, DNS,
  // CORS, timeout). Per the repo error standard (`!err.response` → NETWORK),
  // surface it as a distinct NETWORK error so the UI shows the connection
  // message instead of the generic "unknown error" toast.
  if (!candidate.response) {
    throw new ManagementGuidelineApiError('NETWORK', '네트워크 연결을 확인해주세요.');
  }
  const payload = candidate.response.data?.error;
  throw new ManagementGuidelineApiError(
    (payload?.code as ManagementGuidelineApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

export const managementGuidelinesHttpApi: ManagementGuidelineApi = {
  async list(): Promise<ManagementGuideline[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<ManagementGuideline[]>>(
        endpoints.managementGuidelines,
      );
      const data = res.data?.data;
      // List table iterates this; a non-array body (wrong envelope, HTML, paged
      // wrapper) must surface as a load error rather than white-screen the table.
      if (!Array.isArray(data)) {
        throw new ManagementGuidelineApiError('UNKNOWN', '목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async get(gid: string): Promise<ManagementGuidelineDetail> {
    try {
      const res = await httpClient.get<ApiEnvelope<ManagementGuidelineDetail>>(
        `${endpoints.managementGuidelines}/${gid}`,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async create(input: CreateGuidelineInput): Promise<ManagementGuidelineDetail> {
    try {
      const res = await httpClient.post<ApiEnvelope<ManagementGuidelineDetail>>(
        endpoints.managementGuidelines,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async update(gid: string, input: UpdateGuidelineInput): Promise<ManagementGuidelineDetail> {
    try {
      const res = await httpClient.patch<ApiEnvelope<ManagementGuidelineDetail>>(
        `${endpoints.managementGuidelines}/${gid}`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async remove(gid: string): Promise<DeleteGuidelineResult> {
    try {
      const res = await httpClient.delete<ApiEnvelope<DeleteGuidelineResult>>(
        `${endpoints.managementGuidelines}/${gid}`,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  // PRD §3.2 — the cascade select + PID chips are sourced from the PRO Item
  // Design tree (single source of truth) and mapped into the ProTree wire shape.
  async getProTree(): Promise<ProTree> {
    try {
      const res = await httpClient.get<ApiEnvelope<ProDomain[]>>(
        endpoints.proItemDesign.tree,
      );
      const data = res.data?.data;
      // domainsToProTree walks this array; a non-array body would crash the
      // cascade select. Validate before mapping so it surfaces as a load error.
      if (!Array.isArray(data)) {
        throw new ManagementGuidelineApiError('UNKNOWN', 'PRO 트리를 불러올 수 없습니다.');
      }
      return domainsToProTree(data);
    } catch (e) {
      mapError(e);
    }
  },

  async uploadMedia(file: File): Promise<string> {
    try {
      const form = new FormData();
      form.append('file', file);
      // The shared httpClient sets a default Content-Type: application/json; override it to undefined
      // so Axios detects the FormData and sets multipart/form-data + the boundary itself. Without this
      // the upload goes out as application/json and the multipart endpoint rejects it.
      const res = await httpClient.post<ApiEnvelope<{ url: string; objectKey: string }>>(
        endpoints.uploads,
        form,
        { headers: { 'Content-Type': undefined } },
      );
      return res.data.data.url;
    } catch (e) {
      mapError(e);
    }
  },
};
